import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import { Grid, Card, CardContent, Switch, FormControlLabel, GridSpacing } from '@mui/material';
import { isSafari, isIOS } from 'react-device-detect';
import { SmallPageLineText } from '../@common/PageLineText';
import { getFontSize } from 'utils/style-utils';
import BaseInput from '../@common/BaseInput';
import GrayLine from '../@common/GrayLine';
import { translate } from '../../translations/translator';

const t = key => translate('worksheet', key);

type props = {
  numOfCols: number;
  numOfRows: number;
  handleChange: any;
  valueMap: any;
  showLineToggle?: boolean;
  width?: number;
  height?: number;
  textLinePaddingTop?: number;
  spaceBtwnCols?: GridSpacing | undefined;
  containerPaddingTop?: number;
  isWide?: boolean;
  isExtraWide?: boolean;
  isCursive?: boolean;
  isNarrow?: boolean;
  placeholder?: string;
  alignItemsLeft?: boolean;
  cardMarginTop?: number;
  inputOffset?: number;
  color?: string;
  printGray?: boolean;
  className?: string;
};

const GridWithText = ({
  showLineToggle,
  numOfCols,
  numOfRows,
  handleChange,
  valueMap,
  width = 250,
  height = 108,
  textLinePaddingTop = 44,
  spaceBtwnCols = 0,
  containerPaddingTop,
  isWide,
  isExtraWide,
  isCursive,
  isNarrow,
  placeholder = t('Word'),
  alignItemsLeft,
  cardMarginTop,
  inputOffset = 0,
  color,
  printGray,
  className
}: props) => {
  const [hasLines, setHasLines] = useState(true);
  const classes = useStyles({ width, height, containerPaddingTop, alignItemsLeft, cardMarginTop });

  const rows = [...Array(numOfRows).keys()];
  const cols = [...Array(numOfCols).keys()];

  const fontSize = getFontSize(isCursive, isNarrow, isWide);

  const determineBaseInputMarginTop = () => {
    if (isWide) {
      if (hasLines) {
        return -42;
      }
      return 20;
    }
    if (isCursive) {
      if (!isNarrow && isIOS) {
        if (hasLines) return -16;
        return 30;
      }
      if (hasLines) {
        if (isSafari) return -15;
        return -32;
      }
      if (isSafari) return 31;
      return 14;
    }
    if (hasLines) {
      return -28;
    }
    return 18;
  };

  const baseInputStyles: any = {
    fontFamily: isCursive ? 'Cursive-Book' : 'Print-Wide-Book',
    color: color || '#000',
    style: {
      fontSize: fontSize,
      width: width - 5,
      marginTop: determineBaseInputMarginTop(),
      marginLeft: 15,
      textAlign: 'center'
    }
  };

  return (
    <div className={`${classes.root} ${className}`}>
      {showLineToggle && (
        <div className={clsx(classes.switchDiv, 'no-print')}>
          <FormControlLabel
            control={
              <Switch
                defaultChecked
                checked={hasLines}
                onChange={() => setHasLines(!hasLines)}
                inputProps={{ 'aria-label': t('toggleLines') }}
                color="primary"
              />
            }
            label={t('addOrRemoveLines')}
          />
        </div>
      )}

      {rows.map(row => {
        return (
          <Grid container className={classes.container} spacing={spaceBtwnCols}>
            {cols.map(col => {
              return (
                <Grid key={col} item>
                  <Card className={classes.card}>
                    <CardContent className={classes.cardContent}>
                      {hasLines && (
                        <GrayLine
                          width={width}
                          paddingTop={textLinePaddingTop}
                          isWide={isWide}
                          isExtraWide={isExtraWide}
                          isBlack
                        />
                      )}
                      <SmallPageLineText isNarrow={isNarrow}>
                        <BaseInput
                          onChange={({ target }) =>
                            handleChange(col + 1 + row * numOfRows + inputOffset, target.value)
                          }
                          {...baseInputStyles}
                          placeholder={placeholder}
                          aria-label={`${placeholder} ${col + 1 + row * numOfRows + inputOffset}`}
                          value={valueMap[col + 1 + row * numOfRows + inputOffset]}
                          printGray={printGray}
                        />
                      </SmallPageLineText>
                      {hasLines && (
                        <GrayLine
                          width={width}
                          isNarrow={false}
                          isBlack
                          isWide={isWide}
                          isExtraWide={isExtraWide}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingTop: (props: any) => props.containerPaddingTop ?? 0
  },
  card: {
    width: (props: any) => props.width,
    height: (props: any) => props.height,
    border: '1px solid black',
    boxShadow: 'none !important',
    borderRadius: '0px',
    marginLeft: (props: any) => (props.alignItemsLeft ? '45px' : 0),
    marginTop: (props: any) => props.cardMarginTop
  },
  cardContent: {
    padding: 0
  },
  container: {
    justifyContent: (props: any) => (props.alignItemsLeft ? 'left' : 'center'),
    alignItems: (props: any) => (props.alignItemsLeft ? 'left' : 'center'),
    display: 'flex',
    width: '100%'
  },
  switchDiv: {
    textAlign: 'right'
  }
}));

export default GridWithText;
